import { memo, useEffect, useMemo, useState } from 'react';
import {
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useQuery } from 'react-query';

import useAxiosPrivate from 'hooks/useAxiosPrivate';
import useNotification from 'hooks/useNotification';

const HrConnectModal = ({ open, setOpen, onSuccess }) => {
  const { t } = useTranslation();
  const axiosPrivate = useAxiosPrivate();
  const sendNotification = useNotification();
  const [submitting, setSubmitting] = useState(false);
  const [picks, setPicks] = useState({}); // { [object_id]: user_id }

  // LEFT: HR users list (from external HR system)
  const { data: hrStaff, isLoading: hrLoading } = useQuery({
    queryKey: 'hr/staffes-list',
    queryFn: async () => {
      const response = await axiosPrivate.get('/hr/staffes');
      return (
        response?.data?.data?.data?.data ||
        response?.data?.data?.data ||
        response?.data?.data ||
        []
      );
    },
    enabled: open,
    retry: false,
  });

  // RIGHT: local users dropdown
  const { data: staffes, isLoading: staffLoading } = useQuery({
    queryKey: 'dictionary/staffes-hr-connect',
    queryFn: async () => {
      const response = await axiosPrivate.get('/dictionary/staffes');
      return (
        response?.data?.data?.data?.data ||
        response?.data?.data?.data ||
        response?.data?.data ||
        []
      );
    },
    enabled: open,
    retry: false,
  });

  // /hr/staffes — id = HR id, object_id = bog'langan local user id (null bo'lsa biriktirilmagan)
  const allStaff = useMemo(() => hrStaff || [], [hrStaff]);
  const unconnectedCount = useMemo(
    () => allStaff.filter((u) => !u?.object_id).length,
    [allStaff]
  );

  // initialize picks with currently connected user_ids
  useEffect(() => {
    if (!open) {
      setPicks({});
      return;
    }
    if (allStaff.length) {
      const initial = {};
      allStaff.forEach((u) => {
        if (u?.object_id) initial[u.id] = u.object_id;
      });
      setPicks(initial);
    }
  }, [open, allStaff]);

  const handleClose = () => setOpen(false);

  const handlePick = (objectId, userId) => {
    setPicks((p) => ({ ...p, [objectId]: userId }));
  };

  const handleSubmit = async () => {
    // Only send entries where pick differs from current object_id
    const connections = allStaff
      .filter((u) => {
        const picked = picks[u.id];
        if (!picked) return false;
        return Number(picked) !== Number(u?.object_id);
      })
      .map((u) => ({
        object_id: Number(u.id),
        user_id: Number(picks[u.id]),
      }));

    if (!connections.length) {
      sendNotification({
        msg: t('hr.connect.errors.empty'),
        variant: 'warning',
      });
      return;
    }

    setSubmitting(true);
    try {
      await axiosPrivate.post(
        '/hr/connect',
        JSON.stringify({ connections }),
        { headers: { 'Content-Type': 'application/json' } }
      );
      sendNotification({
        msg: t('hr.connect.alerts.success'),
        variant: 'success',
      });
      onSuccess?.();
      handleClose();
    } catch (e) {
      sendNotification({
        msg: e?.response?.data?.message || e?.message,
        variant: 'error',
      });
    } finally {
      setSubmitting(false);
    }
  };

  const isLoading = hrLoading || staffLoading;

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth='md'
      fullWidth
      PaperProps={{ className: '!base-border !shadow-none' }}
    >
      <DialogTitle className='!bg-gray-100 !mb-2 flex items-center justify-between'>
        <span>
          {t('hr.connect.title')}{' '}
          <span className='text-sm font-normal text-gray-500'>
            ({unconnectedCount}/{allStaff.length})
          </span>
        </span>
        <IconButton size='small' onClick={handleClose}>
          <i className='bi bi-x text-xl' />
        </IconButton>
      </DialogTitle>

      <DialogContent>
        {isLoading ? (
          <div className='flex justify-center py-8'>
            <CircularProgress size={24} />
          </div>
        ) : allStaff.length === 0 ? (
          <div className='py-6 text-center text-gray-400'>
            <i className='bi bi-exclamation-octagon text-2xl mr-2' />
            {t('common.global.noDataFound')}
          </div>
        ) : (
          <div className='flex flex-col gap-2 mt-2'>
            {allStaff.map((u) => {
              const hrId = u?.id;
              const isConnected = !!u?.object_id;
              return (
                <div
                  key={hrId}
                  className={`flex items-center gap-3 rounded-xl border px-3 py-2 ${
                    isConnected
                      ? 'border-green-200 bg-green-50'
                      : 'border-gray-200 bg-white'
                  }`}
                >
                  <div className='flex flex-col flex-1 min-w-0'>
                    <span className='font-medium text-sm text-gray-800 truncate flex items-center gap-1'>
                      {isConnected && (
                        <i className='bi bi-link-45deg text-green-500 text-xs' />
                      )}
                      {u?.name}
                    </span>
                    <span className='text-xs text-gray-400'>HR ID: {hrId}</span>
                  </div>
                  <FormControl size='small' className='!min-w-[260px]'>
                    <InputLabel>{t('common.fields.staff')}</InputLabel>
                    <Select
                      label={t('common.fields.staff')}
                      value={picks[hrId] || ''}
                      onChange={(e) => handlePick(hrId, e.target.value)}
                    >
                      <MenuItem value=''>—</MenuItem>
                      {/* Orphan / missing local user fallback */}
                      {picks[hrId] &&
                        !staffes?.some((s) => s.id === picks[hrId]) && (
                          <MenuItem value={picks[hrId]}>
                            <span className='text-amber-600'>
                              <i className='bi bi-exclamation-triangle mr-1' />
                              ID #{picks[hrId]} ({t('hr.connect.notFound')})
                            </span>
                          </MenuItem>
                        )}
                      {staffes?.map((s) => (
                        <MenuItem key={s.id} value={s.id}>
                          {s.name}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </div>
              );
            })}
          </div>
        )}
      </DialogContent>

      <DialogActions className='!px-4 !pb-4 gap-2'>
        <Button variant='outlined' color='inherit' onClick={handleClose}>
          {t('common.button.cancel')}
        </Button>
        <Button
          variant='contained'
          color='primary'
          onClick={handleSubmit}
          disabled={submitting || allStaff.length === 0}
          startIcon={
            submitting ? <CircularProgress size={14} /> : <i className='bi bi-link-45deg' />
          }
        >
          {t('hr.connect.button.submit')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default memo(HrConnectModal);
